// src/contexts/OrderContext.js
import React, { createContext, useState, useEffect } from "react";
import axios from "axios";

// Create the context
export const OrderContext = createContext();

// Create the provider
export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [storeData, setStoreData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [updateOrderStatusToggle, setUpdateOrderStatusToggle] = useState(false);
  let storeId = null;
  const getToken = () => {
    // Directly return the token from localStorage
    return localStorage.getItem("authToken");
  };

  const fetchOrders = async () => {
    const token = getToken(); // Retrieve the token
    const role = localStorage.getItem("userRole"); // Get the role from localStorage

    const selectedManufacturer = JSON.parse(
      localStorage.getItem("selectedMfr")
    );
    const mfrId = selectedManufacturer?.id; // Safely access the ID
    // The store the user selected at login — disambiguates which account (and
    // thus which karigar_id) to fetch orders for on multi-account mobiles.
    const selectedStoreId = selectedManufacturer?.storeDetails?.id;

    setLoading(true);
    setError(null);

    try {
      if (role === "Karigar") {
        // Fetch orders specific to Karigar
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/orders/getorder?mfr_id=${mfrId}${
          selectedStoreId ? `&storeId=${selectedStoreId}` : ""
        }`;
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const groupedOrders = response.data.data.reduce((acc, group) => {
          acc[group.work_status] = group.orders;
          return acc;
        }, {});
        setOrders(groupedOrders);
      } else if (role === "MFR" || role === "SUPERADMIN" || role === "COMFR") {
        // Fetch all orders for Admin
        const selectedManufacturer = JSON.parse(
          localStorage.getItem("selectedMfr")
        );
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/orders/getorderMfr?mfr_id=${selectedManufacturer.id}`;
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const groupedOrders = response.data.data.reduce((acc, group) => {
          acc[group.work_status] = group.orders;
          return acc;
        }, {});
        setOrders(groupedOrders);
      } else if (role === "Customer") {
        // Fetch orders specific to Manager
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/orders/getorderCustomer?mfr_id=${mfrId}${
          selectedStoreId ? `&storeId=${selectedStoreId}` : ""
        }`;
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const groupedOrders = response.data.data.reduce((acc, group) => {
          acc[group.work_status] = group.orders;
          return acc;
        }, {});
        setOrders(groupedOrders);
      } else if (role === "SEMIMFR") {
        // Fetch orders specific to Manager
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/orders/getorderSemiMfr?mfr_id=${mfrId}`;
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const groupedOrders = response.data.data.reduce((acc, group) => {
          acc[group.work_status] = group.orders;
          return acc;
        }, {});
        setOrders(groupedOrders);
      } else {
        throw new Error("Invalid user role");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreData = async () => {
    const token = getToken(); // Retrieve the token
    const userData = JSON.parse(localStorage.getItem("selectedMfr"));
    storeId = userData?.id; // mfr id — stays the path param (mfr-scoped queries)
    // The actual store the user selected at login (e.g. 3665). Scopes master
    // data (designs/itemtypes/stone/brand) to that store on the backend.
    const selectedStoreId = userData?.storeDetails?.id;
    if (!storeId) {
      console.error("Store ID is not available in localStorage.");
      setError("Store ID not found");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/orders/getDataBystore/${storeId}${
        selectedStoreId ? `?storeId=${selectedStoreId}` : ""
      }`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setStoreData(response.data);
      const Store = response.data.stores[0];
      if (Store) {
        localStorage.setItem("store_id", Store?.id);
        // localStorage.setItem('store', JSON.stringify(Store));
        // console.log(`Store ID ${Store?.id} has been set in localStorage.`);
      }
    } catch (error) {
      console.error("Error fetching store data:", error);
      setError("Failed to fetch store data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.allSettled([fetchStoreData(), fetchOrders()]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [updateOrderStatusToggle, storeId]);

  return (
    <OrderContext.Provider
      value={{
        orders,
        loading,
        error,
        storeData,
        setIsLoggedIn,
        fetchOrders,
        fetchStoreData,
        setStoreData,
        setUpdateOrderStatusToggle,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
